import {useTranslation} from 'react-i18next'
import {useNavigate} from 'react-router-dom'
import {MeasureCard} from '@/features/measures/components/measure-card/MeasureCard.tsx'
import {Button} from '@components/ui/button/Button.tsx'
import {ChevronLeftIcon, FileTextIcon} from '@components/ui/icon/Icon.tsx'
import {routes} from '@utilities/constants/routes.tsx'
import {
    StyledMeasureResult,
    StyledMeasureResultsContainer,
    StyledMeasureResultsWrapper
} from '@pages/measures-result/style.ts'
import {TablePagination} from '@components/ui/table-atoms/TableAtoms.tsx'
import {MeasureFilters} from '@components/ui/measure-filters/MeasureFilters.tsx'
import {useGetMeasures} from '@/features/measures/services/useGetMeasures.ts'
import {Filters, Measure, QuerySearchParams} from '@/features/measures/services/types.ts'
import {useFilterMeasures} from '@hooks/useFilterMeasures.tsx'
import {useSearchParams} from '@hooks/useSearchParams.ts'
import {EmptySearch} from '@components/commons/empty-search/EmptySearch.tsx'
import {useEffect} from 'react'

export const MeasuresResult = () => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const {filters, setFilters} = useFilterMeasures()
    const [searchParams] = useSearchParams(QuerySearchParams)
    const search = searchParams.query || ''

    const {remappedData, meta, isLoading} = useGetMeasures({
        ...filters,
        search,
        orderBy: null,
        orderDirection: 'desc'
    } as Filters)

    const measures = remappedData as Measure[]

    // Update filters when pagination changes
    const handlePageChange = (page: number) => {
        setFilters({
            ...filters,
            page
        })
    }

    const handlePerPageChange = (newPerPage: number) => {
        setFilters({
            ...filters,
            perPage: newPerPage,
            page: 1
        })
    }

    useEffect(() => {
        setFilters({
            ...filters,
            page: filters.page || 1,
            perPage: filters.perPage || 50
        })
    }, [])

    const renderMeasureCards = () => {
        if (isLoading) {
            return Array(4)
                .fill(null)
                .map((_, index) => <MeasureCard key={`skeleton-${index}`} isLoading={true} />)
        }

        if (!measures.length) {
            return (
                <EmptySearch
                    icon={<FileTextIcon fill="none" />}
                    title={t('measures:no_measures')}
                    subtitle={t('measures:try_different_filters')}
                />
            )
        }

        return measures.map(measure => <MeasureCard key={measure.id} measure={measure} hasDetails isLoading={false} />)
    }

    return (
        <StyledMeasureResultsContainer
            gap={6}
            direction={'column'}
            fullWidth
            justify={'flex-start'}
            align={'flex-start'}
        >
            <Button
                variant={'ghost'}
                onClick={() => {
                    const currentParams = new URLSearchParams(window.location.search)
                    const filterParams = new URLSearchParams()

                    // Restore original pagination values if they exist
                    const returnPage = currentParams.get('returnPage')
                    const returnPerPage = currentParams.get('returnPerPage')
                    if (returnPage) filterParams.set('page', returnPage)
                    if (returnPerPage) filterParams.set('perPage', returnPerPage)

                    // Preserve all parameters except query and return values
                    for (const [key, value] of currentParams.entries()) {
                        if (
                            key !== 'query' &&
                            key !== 'returnPage' &&
                            key !== 'returnPerPage' &&
                            key !== 'page' &&
                            key !== 'perPage'
                        ) {
                            filterParams.set(key, value)
                        }
                    }

                    const queryString = filterParams.toString()
                    navigate(`${routes.MEASURES.path}${queryString ? `?${queryString}` : ''}`)
                }}
            >
                <ChevronLeftIcon />
                {t('measures:measures')}
            </Button>
            <StyledMeasureResultsWrapper>
                <div>
                    <MeasureFilters />
                </div>

                <StyledMeasureResult $emptyMeasure={measures.length === 0 && !isLoading}>
                    {renderMeasureCards()}
                </StyledMeasureResult>

                {measures.length > 0 && (
                    <TablePagination
                        isSticky={false}
                        isLoading={isLoading}
                        pagination={{
                            limit: filters.perPage || 50,
                            currentPage: meta?.currentPage || filters.page || 1,
                            total: meta?.total || 100,
                            onChangePage: handlePageChange,
                            onChangeLimit: handlePerPageChange
                        }}
                    />
                )}
            </StyledMeasureResultsWrapper>
        </StyledMeasureResultsContainer>
    )
}
